import React from 'react';

/**
 * BrandLogo — the app-wide brand stamp. Replaces the italic "Be Bold"
 * wordmark used across splash screens, ScreenHeader eyebrows, and
 * onboarding preludes with the canonical /logo.{webp,png} asset.
 *
 * Keep every brand stamp in one place so logo sizing, alt text, and
 * display box stay consistent across Profile / Progress / Calendar /
 * Learning / LanguageSelector / ConsentMessage / ErrorBoundary.
 *
 * Props:
 *   size   number — px width, height is auto to preserve aspect ratio
 *                   (default 40, sized for ScreenHeader eyebrow use).
 *   style  extra inline styles (margin etc).
 */
export default function BrandLogo({ size = 40, style }) {
  // <picture> with a srcset that lets the browser pick the smallest
  // WebP that still covers the rendered size at the device's pixel
  // ratio:
  //   - 96 w  (1 KB)  — covers 1x screens up to 96 px and 2x up to 48 px
  //   - 192 w (3 KB)  — covers 2x screens up to 96 px (most usage)
  //   - 500 w (9 KB)  — only fetched for the auth-screen 176 px stamp on
  //                     2x retina, where the browser actually needs ~352
  //                     pixels of resolution
  // PNG kept as fallback for Safari < 14.
  return (
    <picture>
      <source
        type="image/webp"
        srcSet="/logo-96.webp 96w, /logo-192.webp 192w, /logo.webp 500w"
        sizes={`${size}px`}
      />
      <img
        src="/logo.png"
        alt="Be Bold"
        width={size}
        height={size}
        style={{
          width: size,
          height: 'auto',
          display: 'block',
          ...style,
        }}
      />
    </picture>
  );
}
